(() => {
  const root = document.querySelector('[data-pdp-selectors]');
  if (!root) return;

  // cache DOM
  const groups = Array.from(root.querySelectorAll('[data-option-group]'));
  const allButtons = Array.from(root.querySelectorAll('[data-option-btn]'));
  const buttonsByIndex = new Map();

  for (const btn of allButtons) {
    const idx = Number(btn.getAttribute('data-option-index'));
    if (!buttonsByIndex.has(idx)) buttonsByIndex.set(idx, []);
    buttonsByIndex.get(idx).push(btn);
  }

  const discontinuedColors = new Set(window.__pdpDiscontinuedColors || []);

  function getStore() {
    return window.Alpine?.store?.('pdp') ?? null;
  }

  function getSelectedOptionsFromUI() {
    // Seed with the current variant's options so hidden single-value options
    // (filtered out by option.values.size > 1 in Liquid) don't leave gaps that
    // break the variant key lookup in getVariantByOptions.
    const store = getStore();
    const currentVariant = store ? store.getVariantById(store.variantId) : null;
    const selected = currentVariant ? [...currentVariant.options] : [];
    for (const group of groups) {
      const idx = Number(group.getAttribute('data-option-index'));
      const pressed = group.querySelector('[data-option-btn][aria-pressed="true"]');
      if (pressed) selected[idx] = pressed.getAttribute('data-option-value');
    }
    return selected;
  }

  function setPressed(optionIndex, optionValue) {
    const buttons = buttonsByIndex.get(optionIndex) || [];
    for (const btn of buttons) {
      const isMatch = btn.getAttribute('data-option-value') === optionValue;
      const next = isMatch ? 'true' : 'false';
      if (btn.getAttribute('aria-pressed') !== next) btn.setAttribute('aria-pressed', next);
    }
  }

  function updateSelectedLabels() {
    for (const group of groups) {
      const pressed = group.querySelector('[data-option-btn][aria-pressed="true"]');
      const label = group.querySelector('[data-selected-value]');
      if (!pressed) continue;

      // If the pressed swatch belongs to a unique-options category (e.g. Limited),
      // suppress the value here — the suffix is shown next to the category label instead.
      const inCategory = !!pressed.closest('[data-category]');

      if (label) {
        const next = inCategory ? '' : (pressed.getAttribute('data-option-value') || '');
        if (label.textContent !== next) label.textContent = next;
      }
    }
  }

  // Appends " <selected value>" to the unique-options category label (e.g.
  // "Limited") when the currently-pressed color swatch belongs to that
  // category; clears the suffix on all other category labels.
  function updateCategoryLabels() {
    const pressedColor = root.querySelector(
      '[data-option-btn][aria-pressed="true"][data-color-option]'
    );
    const categoryHost = pressedColor?.closest('[data-category]');
    const pressedCategory = categoryHost?.getAttribute('data-category') || null;
    const pressedValue = pressedColor?.getAttribute('data-option-value') || '';

    for (const label of root.querySelectorAll('[data-category-label]')) {
      const slot = label.querySelector('[data-category-selected]');
      if (!slot) continue;
      const cat = label.getAttribute('data-category');
      const next = cat && cat === pressedCategory ? ` ${pressedValue}` : '';
      if (slot.textContent !== next) slot.textContent = next;
    }
  }

  let variantIndex = null;

  function buildVariantIndex(store) {
    const variants = store?.product?.variants || [];
    return variants.map((v) => ({
      id: v.id,
      options: v.options || [],
      available: v?.available !== false,
    }));
  }

  function doesOptionValueExistFast(optionIndex, optionValue, selected) {
    const variants = variantIndex || [];
    if (!variants.length) return true;

    return variants.some((v) => {
      const opts = v.options;
      if (opts[optionIndex] !== optionValue) return false;

      for (let i = 0; i < selected.length; i++) {
        if (i === optionIndex) continue;
        const sel = selected[i];
        if (sel == null) continue;
        if (opts[i] !== sel) return false;
      }
      return true;
    });
  }

  function isVariantLocationAvailable(variantId) {
    const store = getStore();
    const levels = store?.product?.inventoryLevels?.[variantId];
    // If inventory data hasn't loaded yet, assume available
    if (!levels || !levels.inventory) return true;

    // BOPIS: availability is driven solely by in-range pickup stock, computed from
    // store coordinates on the pdp store — online inventory never counts.
    if (store?.bopisMode) {
      return store.isVariantPickupAvailable(variantId);
    }

    for (const [storeName, qty] of Object.entries(levels.inventory)) {
      if (storeName === 'Cozy Earth Online' && qty > 0) return true;
      const dist = levels.distances?.[storeName];
      if (qty > 0 && dist != null && dist <= 50) return true;
    }
    return false;
  }

  function shouldHideColor(optionIndex, colorValue) {
    if (!discontinuedColors.has(colorValue)) return false;

    const variants = variantIndex || [];
    if (!variants.length) return false;

    // Inventory hasn't loaded yet — show by default; re-evaluates on pdp:inventory-loaded
    const store = getStore();
    const levels = store?.product?.inventoryLevels;
    if (!levels || Object.keys(levels).length === 0) return false;

    const bopis = !!store?.bopisMode;

    // Normally a discontinued color needs online stock to stay visible — retail-only
    // stock does not make it available on the online storefront. In BOPIS mode the
    // reverse applies: only in-range pickup stock keeps it visible.
    const hasStock = variants.some((v) => {
      if (v.options[optionIndex] !== colorValue) return false;
      if (bopis) return isVariantLocationAvailable(v.id);
      const inv = levels[v.id];
      if (!inv || !inv.inventory) return false;
      const onlineQty = inv.inventory['Cozy Earth Online'];
      if (onlineQty == null) return false;
      return onlineQty > 0;
    });

    return !hasStock;
  }

  function isOptionValueAvailable(optionIndex, optionValue, selected) {
    const variants = variantIndex || [];
    if (!variants.length) return true;

    return variants.some((v) => {
      if (!v.available) return false;

      const opts = v.options;
      if (opts[optionIndex] !== optionValue) return false;

      for (let i = 0; i < selected.length; i++) {
        if (i === optionIndex) continue;
        const sel = selected[i];
        if (sel == null) continue;
        if (opts[i] !== sel) return false;
      }

      // Also check location-based inventory from the store
      if (!isVariantLocationAvailable(v.id)) return false;

      return true;
    });
  }

  function updateDisabledStates() {
    const store = getStore();
    if (!store) return;
    if (!variantIndex) variantIndex = buildVariantIndex(store);

    const selected = getSelectedOptionsFromUI();

    for (const btn of allButtons) {
      const isColor = btn.hasAttribute('data-color-option');

      if (isColor) {
        const optionIndex = Number(btn.getAttribute('data-option-index'));
        const colorValue = btn.getAttribute('data-option-value');
        const hide = shouldHideColor(optionIndex, colorValue);

        // Buttons start with the HTML `hidden` attribute set in Liquid; use
        // btn.hidden (not classList) so we toggle the attribute, not a CSS class.
        if (btn.hidden !== hide) {
          btn.hidden = hide;
        }

        // Cross out the swatch only if EVERY size for this color is
        // unavailable — aggregate across all sizes rather than the
        // currently-selected one. Skip if discontinued (already handled).
        // If inventory hasn't loaded yet, assume available — re-runs on
        // pdp:inventory-loaded.
        let outOfStock = false;
        if (!hide && !discontinuedColors.has(colorValue)) {
          const levels = store.product?.inventoryLevels;
          if (levels) {
            outOfStock = !(variantIndex || []).some((v) => {
              if (v.options[optionIndex] !== colorValue) return false;
              return isVariantLocationAvailable(v.id);
            });
          }
        }

        btn.disabled = false;
        const nextDisabled = outOfStock ? '1' : '0';
        if (btn.dataset.disabled !== nextDisabled) {
          btn.dataset.disabled = nextDisabled;
          btn.classList.toggle('opacity-70', outOfStock);
          btn.classList.toggle('is-disabled', outOfStock);
          btn.setAttribute('aria-disabled', outOfStock ? 'true' : 'false');
        }
        continue;
      }

      const optionIndex = Number(btn.getAttribute('data-option-index'));
      const optionValue = btn.getAttribute('data-option-value');

      const exists = doesOptionValueExistFast(optionIndex, optionValue, selected);

      // hide the non color vairant selector ONLY if it does not exist for the current selection
      const shouldHide = !exists;
      if (btn.classList.contains('hidden') !== shouldHide) {
        btn.classList.toggle('hidden', shouldHide);
      }

      let outOfStock = false;
      if (exists) {
        outOfStock = !isOptionValueAvailable(optionIndex, optionValue, selected);
      }

      btn.disabled = false;

      const nextDisabled = outOfStock ? '1' : '0';
      if (btn.dataset.disabled !== nextDisabled) {
        btn.dataset.disabled = nextDisabled;
        btn.classList.toggle('opacity-70', outOfStock);
        btn.classList.toggle('is-disabled', outOfStock);
        btn.setAttribute('aria-disabled', outOfStock ? 'true' : 'false');
      }
    }
  }

  function syncUIToVariant(variantId) {
    const store = getStore();
    const variant = store.getVariantById(variantId);

    // Only re-press groups the customer has already interacted with.
    // Unpressed groups represent options the customer hasn't picked
    // yet — don't auto-press them just because the click handler's
    // partial-selection fallback set variantId to a fully-qualified
    // variant for the media gallery's benefit.
    variant.options.forEach((val, idx) => {
      const group = groups.find(
        (g) => Number(g.getAttribute('data-option-index')) === idx
      );
      if (!group?.querySelector('[data-option-btn][aria-pressed="true"]')) return;
      setPressed(idx, val);
    });
    updateSelectedLabels();
    updateCategoryLabels();
    updateDisabledStates();
  }

  /**
   * Broadcast the user's current selection state so the Add to Bag form
   * knows whether to enable the submit button or show "Select a [option]".
   * Detail shape:
   *   {
   *     selected: [val | null, ...]      // by option index
   *     allSelected: boolean
   *     missing:  ['Color', 'Size']      // option names not yet picked
   *   }
   */
  function dispatchSelectionState() {
    const selected = getSelectedOptionsFromUI();
    const missing = [];
    for (const group of groups) {
      const idx = Number(group.getAttribute('data-option-index'));
      if (selected[idx] == null) {
        missing.push(group.getAttribute('data-option-name'));
      }
    }
    const allSelected = missing.length === 0 && groups.length > 0;
    window.dispatchEvent(
      new CustomEvent('pdp:selection-change', {
        detail: { selected, allSelected, missing },
      })
    );
  }

  // Make every button inside a [data-equal-width-group] the same width,
  // sized to the widest natural content. Floor at 48px so short-text
  // groups (XS/S/M) still get a comfortable minimum.
  const MIN_BUTTON_WIDTH_PX = 48;
  function equalizeGroupWidths() {
    const equalGroups = root.querySelectorAll('[data-equal-width-group]');
    for (const group of equalGroups) {
      const buttons = Array.from(group.querySelectorAll('[data-option-btn]'));
      if (buttons.length === 0) continue;

      for (const btn of buttons) btn.style.minWidth = '';

      let max = 0;
      for (const btn of buttons) {
        const w = btn.getBoundingClientRect().width;
        if (w > max) max = w;
      }
      const target = Math.max(max, MIN_BUTTON_WIDTH_PX);
      for (const btn of buttons) btn.style.minWidth = `${target}px`;
    }
  }

  function init() {
    const store = getStore();
    if (!store?.product) return;

    variantIndex = buildVariantIndex(store);

    if (!store.variantId) {
      const initial = Number(root.getAttribute('data-initial-variant-id'));
      if (Number.isFinite(initial)) store.variantId = initial;
    }

    // Don't pre-press swatches based on the initial variant — UX wants
    // "no swatch selected" until the user picks one. We still need to
    // initialize disabled states (so out-of-stock colors are crossed out
    // before the customer interacts) and broadcast the empty selection
    // state so the Add to Bag form starts in its "Select a Color/Size"
    // state.
    updateDisabledStates();
    dispatchSelectionState();
    equalizeGroupWidths();

    // Exception: if the page was loaded with a ?variant= URL param
    // (e.g. arriving from a swatch link on a related PDP), pre-select
    // that variant's swatches so the highlighted swatch matches the
    // images already showing on screen.
    const urlVariantParam = new URLSearchParams(window.location.search).get('variant');
    if (urlVariantParam) {
      const urlVariantId = Number(urlVariantParam);
      if (Number.isFinite(urlVariantId)) {
        const variant = store.getVariantById(urlVariantId);
        if (variant) {
          variant.options.forEach((val, idx) => {
            // Only pre-select color swatches — leave size unselected
            // so the customer still has to make an intentional size pick.
            const buttons = buttonsByIndex.get(idx) || [];
            const isColorOption = buttons.some(btn => btn.hasAttribute('data-color-option'));
            if (isColorOption) setPressed(idx, val);
          });
          updateSelectedLabels();
          updateCategoryLabels();
          updateDisabledStates();
          dispatchSelectionState();
        }
      }
    }

    // Add to Bag clicked with options still missing: show the callout on the
    // first missing group and bring it into view. Hide each callout as soon
    // as its option gets picked.
    window.addEventListener('pdp:selection-required', (ev) => {
      const missing = ev?.detail?.missing || [];
      const group = groups.find((g) => missing.includes(g.getAttribute('data-option-name')));
      const callout = group?.querySelector('[data-selection-callout]');
      if (!callout) return;
      callout.hidden = false;
      const rect = group.getBoundingClientRect();
      if (rect.top < 0 || rect.top > window.innerHeight * 0.75) {
        group.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    window.addEventListener('pdp:selection-change', () => {
      for (const group of groups) {
        const callout = group.querySelector('[data-selection-callout]');
        if (callout && !callout.hidden && group.querySelector('[data-option-btn][aria-pressed="true"]')) {
          callout.hidden = true;
        }
      }
    });

    window.addEventListener('pdp:variant-change', (ev) => {
      const nextVariant = ev?.detail?.variant;
      if (nextVariant?.id) {
        syncUIToVariant(nextVariant.id);
        dispatchSelectionState();
      }
    });

    // Re-equalize on viewport resize (debounced) — the widest content
    // doesn't change, but font metrics/available space can at breakpoints.
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(equalizeGroupWidths, 100);
    });

    // Re-evaluate disabled states once inventory data loads from the ATC form
    window.addEventListener(
      'pdp:inventory-loaded',
      () => {
        updateDisabledStates();
      },
      { once: true }
    );

    // In BOPIS mode, availability is keyed to the selected pickup store. Re-run the
    // swatch states whenever that store changes — when it first resolves after
    // geolocation, or when the shopper switches stores via "Change store".
    if (window.Alpine?.effect) {
      window.Alpine.effect(() => {
        // Touch the reactive fields so this effect re-runs when they change.
        void store.bopisMode;
        void store.bopisStore;
        updateDisabledStates();
      });
    }

    // Fetch inventory for the see_more (connected) product and show/hide
    // discontinued swatch links based on "Cozy Earth Online" stock only.
    const seeMoreCarousel = root.querySelector('[data-see-more-product-id]');
    if (seeMoreCarousel) {
      const seeMoreProductId = seeMoreCarousel.getAttribute('data-see-more-product-id');
      const seeMoreColorIdx = Number(seeMoreCarousel.getAttribute('data-see-more-color-index'));
      const discLinks = Array.from(seeMoreCarousel.querySelectorAll('[data-see-more-disc]'));

      if (discLinks.length && seeMoreProductId) {
        const variantScript = document.getElementById('see-more-variants-json-' + seeMoreProductId);
        const seeMoreVariants = variantScript ? JSON.parse(variantScript.textContent) : [];

        fetch('https://cozy-earth-api.fly.dev/v1/inventory-levels?productId=' + seeMoreProductId)
          .then((r) => r.ok ? r.json() : Promise.reject(r.status))
          .then((data) => {
            const levels = {};
            data.forEach((v) => { levels[v.variantId] = v.inventory; });

            discLinks.forEach((link) => {
              const colorValue = link.getAttribute('data-color-option');
              const hasOnlineStock = seeMoreVariants.some((v) => {
                if (v.options[seeMoreColorIdx] !== colorValue) return false;
                const inv = levels[v.id];
                if (!inv) return false;
                const qty = inv['Cozy Earth Online'];
                if (qty == null) return false;
                return qty > 0;
              });
              link.hidden = !hasOnlineStock;
            });
          })
          .catch(() => {
            // On error, show all discontinued links rather than hiding them
            discLinks.forEach((link) => { link.hidden = false; });
          });
      }
    }

    // Hover/focus/touch prefetch: when the customer's pointer hits a
    // color swatch, ask the media gallery to warm that color's images
    // so the imminent click paints from a decoded cache. Deduped per
    // swatch. Touchstart gets us a head start of ~150-300ms over click.
    const prefetched = new Set();
    const prefetchForSwatch = (btn) => {
      const optionGroup = btn.closest('[data-option-group]');
      const optionName = optionGroup?.getAttribute('data-option-name');
      const optionValue = btn.getAttribute('data-option-value');
      if (!optionName || !optionValue) return;
      const key = `${optionName}|${optionValue}`;
      if (prefetched.has(key)) return;
      prefetched.add(key);
      window.dispatchEvent(
        new CustomEvent('pdp:swatch-prefetch', {
          detail: { optionName, optionValue },
        })
      );
    };
    for (const btn of allButtons) {
      if (!btn.hasAttribute('data-color-option')) continue;
      const handler = () => prefetchForSwatch(btn);
      btn.addEventListener('pointerenter', handler);
      btn.addEventListener('focus', handler);
      btn.addEventListener('touchstart', handler, { passive: true });
    }
  }

  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-option-btn]');
    if (!btn || btn.disabled) return;

    const store = getStore();
    if (!store?.product) return;

    const optionIndex = Number(btn.getAttribute('data-option-index'));
    const optionValue = btn.getAttribute('data-option-value');

    setPressed(optionIndex, optionValue);
    updateDisabledStates();
    updateSelectedLabels();
    updateCategoryLabels();

    // Find a variant for the current picks. Exact match when the
    // customer has chosen everything; otherwise fall back to a partial
    // match — but ONLY when a color swatch is already pressed. The
    // fallback exists so the media gallery can swap to the chosen
    // color's images mid-selection; firing it on a size-only click
    // would change variantId to some default-color variant and swap
    // images to a color the customer never picked. setVariantId fires
    // BEFORE dispatchSelectionState so the form's variant-change
    // handler sees the new variant first.
    const selected = getSelectedOptionsFromUI();
    let variant = store.getVariantByOptions(selected);

    if (!variant) {
      const colorPressed = root.querySelector(
        '[data-option-btn][aria-pressed="true"][data-color-option]'
      );
      if (colorPressed) {
        const variants = store.product?.variants || [];
        const matchesPicked = (v) =>
          selected.every((val, i) => val == null || v.options[i] === val);
        variant =
          variants.find((v) => v.available && matchesPicked(v)) ||
          variants.find(matchesPicked) ||
          null;
      }
    }

    if (variant?.id) store.setVariantId(variant.id);
    dispatchSelectionState();
  });

  document.addEventListener('alpine:initialized', init, { once: true });
})();
